import { useState } from "react";
import { X, Plus } from "lucide-react";
import { LeadCaptureField, GhlCustomField } from "../types";
import { useAppServices } from "../hook/services";

// Standard GHL contact fields offered in lead-capture builders. Mirrors the
// backend catalog in lib/leadFields.js.
export const STANDARD_LEAD_FIELDS: { key: string; label: string }[] = [
  { key: "name", label: "Full name" },
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "email", label: "Email address" },
  { key: "phone", label: "Phone number" },
  { key: "companyName", label: "Company" },
  { key: "address1", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State / region" },
  { key: "postalCode", label: "Postal code" },
  { key: "country", label: "Country" },
  { key: "website", label: "Website" },
];
const STANDARD_LABEL: Record<string, string> = Object.fromEntries(
  STANDARD_LEAD_FIELDS.map((f) => [f.key, f.label]),
);

// A reusable field builder for lead-capture forms: pick standard contact fields
// or CRM custom fields, set each field's label + required, remove, etc.
const LeadFieldsEditor = ({
  fields,
  onChange,
  customFields,
}: {
  fields: LeadCaptureField[];
  onChange: (fields: LeadCaptureField[]) => void;
  customFields: GhlCustomField[];
}) => {
  const services = useAppServices();
  // Custom fields created inline are kept here so they show up immediately
  // (the parent's list isn't refetched).
  const [created, setCreated] = useState<GhlCustomField[]>([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const allCustom = [...customFields, ...created];

  const fieldName = (f: LeadCaptureField) =>
    f.custom
      ? allCustom.find((c) => c.id === f.key)?.name || "Custom field"
      : STANDARD_LABEL[f.key] || f.key;

  const createField = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setErr("");
    const { response, error } = await services.ghl.create_custom_field({
      payload: { name },
    });
    setBusy(false);
    if (error || !response?.id) {
      setErr(error?.message || "Couldn't create the field. Try a different name.");
      return;
    }
    const cf: GhlCustomField = response;
    setCreated((x) => [...x, cf]);
    onChange([
      ...fields,
      { key: cf.id, custom: true, label: cf.name, dataType: cf.dataType },
    ]);
    setCreating(false);
    setNewName("");
  };

  const add = (token: string) => {
    const sep = token.indexOf(":");
    const kind = token.slice(0, sep);
    const key = token.slice(sep + 1);
    if (!key || fields.some((f) => f.key === key)) return;
    if (kind === "cf") {
      const cf = allCustom.find((c) => c.id === key);
      onChange([
        ...fields,
        { key, custom: true, label: cf?.name, dataType: cf?.dataType },
      ]);
    } else {
      onChange([...fields, { key, custom: false }]);
    }
  };
  const remove = (i: number) => onChange(fields.filter((_, idx) => idx !== i));
  const update = (i: number, patch: Partial<LeadCaptureField>) =>
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  return (
    <div>
      <div className="space-y-1.5">
        {fields.map((f, i) => (
          <div
            key={f.key}
            className="flex items-center gap-2 bg-white rounded-lg ring-1 ring-ink-200 pl-2 pr-1.5 py-1.5"
          >
            <input
              type="text"
              value={f.label ?? ""}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder={fieldName(f)}
              className="flex-1 min-w-0 text-xs bg-transparent focus:outline-none placeholder:text-ink-400"
            />
            {f.custom && (
              <span className="text-[10px] text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded">
                Custom
              </span>
            )}
            <label className="flex items-center gap-1 text-[11px] text-ink-500 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={!!f.required}
                onChange={(e) => update(i, { required: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-ink-300 text-brand-600 focus:ring-1 focus:ring-brand-500/30"
              />
              Required
            </label>
            <button
              type="button"
              onClick={() => remove(i)}
              title="Remove field"
              className="p-1 text-ink-400 hover:text-rose-600 rounded shrink-0"
            >
              <X size={13} />
            </button>
          </div>
        ))}
        {!fields.length && (
          <div className="text-[11px] text-ink-400 italic">
            Add at least one field to collect.
          </div>
        )}
      </div>
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) add(e.target.value);
          e.currentTarget.selectedIndex = 0;
        }}
        className="mt-2 w-full h-8 px-2 bg-white border border-ink-200 rounded-lg text-xs text-ink-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
      >
        <option value="">+ Add a field…</option>
        <optgroup label="Contact fields">
          {STANDARD_LEAD_FIELDS.filter(
            (s) => !fields.some((f) => !f.custom && f.key === s.key),
          ).map((s) => (
            <option key={s.key} value={`std:${s.key}`}>
              {s.label}
            </option>
          ))}
        </optgroup>
        {allCustom.length > 0 && (
          <optgroup label="Custom fields">
            {allCustom
              .filter((c) => !fields.some((f) => f.custom && f.key === c.id))
              .map((c) => (
                <option key={c.id} value={`cf:${c.id}`}>
                  {c.name}
                </option>
              ))}
          </optgroup>
        )}
      </select>

      {/* Create a brand-new custom field in the CRM, inline. */}
      {creating ? (
        <div className="mt-1.5">
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={newName}
              autoFocus
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  createField();
                }
              }}
              placeholder="New custom field name"
              className="flex-1 h-8 px-2 text-xs bg-white border border-ink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            />
            <button
              type="button"
              disabled={busy || !newName.trim()}
              onClick={createField}
              className="h-8 px-2.5 text-xs font-semibold rounded-lg bg-brand-600 text-white disabled:opacity-50"
            >
              {busy ? "…" : "Create"}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setNewName("");
                setErr("");
              }}
              className="h-8 px-2 text-xs text-ink-400 hover:text-ink-700"
            >
              Cancel
            </button>
          </div>
          {err && <div className="mt-1 text-[11px] text-rose-600">{err}</div>}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-brand-600 hover:text-brand-700 font-medium"
        >
          <Plus size={12} /> Create a new custom field
        </button>
      )}

      {/* A contact needs email or phone to match/upsert in the CRM. */}
      {fields.length > 0 &&
        !fields.some((f) => !f.custom && (f.key === "email" || f.key === "phone")) && (
          <div className="mt-2 text-[11px] text-amber-600">
            Add <strong>Email</strong> or <strong>Phone</strong> so submissions
            can be saved to a contact — without one, the CRM can't match them.
          </div>
        )}
    </div>
  );
};

export default LeadFieldsEditor;
